import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import type { ClientTypeRecord } from "@shared/schema";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, DollarSign, Percent, Calendar, AlertTriangle, RefreshCw, MapPin, Check, ChevronsUpDown } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";

interface LateFeeTier {
  id: string;
  name: string;
  clientType: string;
  daysAfterEvent: number;
  feeType: string;
  feeAmount: string;
  isRecurring: boolean;
  recurringIntervalDays: number | null;
  gracePeriodDays: number;
  isCumulative: boolean;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

interface StateException {
  id: string;
  state: string;
  clientType: string;
  maxFeeAmount: string | null;
  maxFeePercent: string | null;
  isRecurring: boolean;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface LateFeeSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inPanel?: boolean;
}


const US_STATES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DC", label: "Washington D.C." },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

export function LateFeeSettingsDialog({ open, onOpenChange, inPanel = false }: LateFeeSettingsDialogProps) {
  const { toast } = useToast();
  const [selectedClientType, setSelectedClientType] = useState("Senior Living");
  const [editingTier, setEditingTier] = useState<LateFeeTier | null>(null);
  const [editingException, setEditingException] = useState<StateException | null>(null);
  const [isCreatingTier, setIsCreatingTier] = useState(false);
  const [isCreatingException, setIsCreatingException] = useState(false);
  
  const [tierForm, setTierForm] = useState({
    name: "",
    daysAfterEvent: 60,
    feeType: "flat",
    feeAmount: "100.00",
    isRecurring: false,
    recurringIntervalDays: 30,
    gracePeriodDays: 0,
    isCumulative: false,
    isActive: true,
  });

  const [exceptionForm, setExceptionForm] = useState({
    state: "",
    maxFeeAmount: "",
    maxFeePercent: "",
    isRecurring: false,
    notes: "",
    isActive: true,
  });

  const { data: tiers = [], isLoading: tiersLoading } = useQuery<LateFeeTier[]>({
    queryKey: ["/api/late-fee-tiers"],
    enabled: open,
  });

  const { data: exceptions = [], isLoading: exceptionsLoading } = useQuery<StateException[]>({
    queryKey: ["/api/late-fee-state-exceptions"],
    enabled: open,
  });

  const { data: clientTypesData = [] } = useQuery<ClientTypeRecord[]>({
    queryKey: ["/api/client-types"],
    enabled: open,
  });

  const CLIENT_TYPES = useMemo(() => {
    return clientTypesData
      .filter(ct => ct.isActive)
      .map(ct => ({ value: ct.name, label: ct.name }));
  }, [clientTypesData]);

  // Filter tiers by selected client type
  const filteredTiers = tiers.filter(t => t.clientType === selectedClientType);
  const filteredExceptions = exceptions.filter(e => e.clientType === selectedClientType);

  const createTierMutation = useMutation({
    mutationFn: async (data: typeof tierForm & { clientType: string; sortOrder: number }) => {
      const response = await apiRequest("POST", "/api/late-fee-tiers", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/late-fee-tiers"] });
      toast({ title: "Fee Rule Created", description: "The late fee rule has been created." });
      resetTierForm();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to create fee rule.", variant: "destructive" });
    },
  });

  const updateTierMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof tierForm }) => {
      const response = await apiRequest("PATCH", `/api/late-fee-tiers/${id}`, data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/late-fee-tiers"] });
      toast({ title: "Fee Rule Updated", description: "The late fee rule has been updated." });
      resetTierForm();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update fee rule.", variant: "destructive" });
    },
  });

  const deleteTierMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/late-fee-tiers/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/late-fee-tiers"] });
      toast({ title: "Fee Rule Deleted", description: "The late fee rule has been removed." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to delete fee rule.", variant: "destructive" });
    },
  });

  const createExceptionMutation = useMutation({
    mutationFn: async (data: typeof exceptionForm & { clientType: string }) => {
      const response = await apiRequest("POST", "/api/late-fee-state-exceptions", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/late-fee-state-exceptions"] });
      toast({ title: "State Exception Created", description: "The state exception has been created." });
      resetExceptionForm();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to create exception.", variant: "destructive" });
    },
  });

  const updateExceptionMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof exceptionForm }) => {
      const response = await apiRequest("PATCH", `/api/late-fee-state-exceptions/${id}`, data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/late-fee-state-exceptions"] });
      toast({ title: "State Exception Updated", description: "The state exception has been updated." });
      resetExceptionForm();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update exception.", variant: "destructive" });
    },
  });

  const deleteExceptionMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/late-fee-state-exceptions/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/late-fee-state-exceptions"] });
      toast({ title: "State Exception Deleted", description: "The state exception has been removed." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to delete exception.", variant: "destructive" });
    },
  });

  const resetTierForm = () => {
    setTierForm({
      name: "",
      daysAfterEvent: 60,
      feeType: "flat",
      feeAmount: "100.00",
      isRecurring: false,
      recurringIntervalDays: 30,
      gracePeriodDays: 0,
      isCumulative: false,
      isActive: true,
    });
    setEditingTier(null);
    setIsCreatingTier(false);
  };

  const resetExceptionForm = () => {
    setExceptionForm({
      state: "",
      maxFeeAmount: "",
      maxFeePercent: "",
      isRecurring: false,
      notes: "",
      isActive: true,
    });
    setEditingException(null);
    setIsCreatingException(false);
  };

  const startEditTier = (tier: LateFeeTier) => {
    setEditingTier(tier);
    setTierForm({
      name: tier.name,
      daysAfterEvent: tier.daysAfterEvent,
      feeType: tier.feeType || "flat",
      feeAmount: tier.feeAmount,
      isRecurring: tier.isRecurring || false,
      recurringIntervalDays: tier.recurringIntervalDays || 30,
      gracePeriodDays: tier.gracePeriodDays,
      isCumulative: tier.isCumulative,
      isActive: tier.isActive,
    });
    setIsCreatingTier(false);
  };

  const startEditException = (exception: StateException) => {
    setEditingException(exception);
    setExceptionForm({
      state: exception.state,
      maxFeeAmount: exception.maxFeeAmount || "",
      maxFeePercent: exception.maxFeePercent || "",
      isRecurring: exception.isRecurring || false,
      notes: exception.notes || "",
      isActive: exception.isActive,
    });
    setIsCreatingException(false);
  };

  const handleSaveTier = () => {
    if (!tierForm.name.trim()) {
      toast({ title: "Error", description: "Name is required.", variant: "destructive" });
      return;
    }

    if (tierForm.daysAfterEvent < 0) {
      toast({ title: "Error", description: "Days after show must be 0 or greater.", variant: "destructive" });
      return;
    }

    if (parseFloat(tierForm.feeAmount) <= 0) {
      toast({ title: "Error", description: "Fee amount must be greater than 0.", variant: "destructive" });
      return;
    }
    
    if (editingTier) {
      updateTierMutation.mutate({ id: editingTier.id, data: tierForm });
    } else {
      createTierMutation.mutate({ ...tierForm, clientType: selectedClientType, sortOrder: tiers.length });
    }
  };

  const handleSaveException = () => {
    if (!exceptionForm.state) {
      toast({ title: "Error", description: "State is required.", variant: "destructive" });
      return;
    }

    if (!exceptionForm.maxFeeAmount && !exceptionForm.maxFeePercent) {
      toast({ title: "Error", description: "Either max fee amount or max fee percentage is required.", variant: "destructive" });
      return;
    }
    
    if (editingException) {
      updateExceptionMutation.mutate({ id: editingException.id, data: exceptionForm });
    } else {
      createExceptionMutation.mutate({ ...exceptionForm, clientType: selectedClientType });
    }
  };

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === "string" ? parseFloat(amount) : amount;
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(num);
  };

  const getClientTypeLabel = (value: string) => {
    return CLIENT_TYPES.find(ct => ct.value === value)?.label || value;
  };

  const getStateLabel = (value: string) => {
    return US_STATES.find(s => s.value === value)?.label || value;
  };

  const isLoading = tiersLoading || exceptionsLoading;

  const dialogClass = "w-full sm:max-w-3xl max-h-[85vh] overflow-y-auto";
  const innerContent = (<>
      <DialogHeader>
        <DialogTitle>Late Fee Schedule</DialogTitle>
        <DialogDescription>
          Configure late fees by client type. Set when fees apply after the event date, and add state-specific exceptions.
        </DialogDescription>
      </DialogHeader>

        <div className="space-y-6">
          {/* Client Type Selector */}
          <div className="space-y-2">
            <Label className="text-base font-medium">Client Type</Label>
            <Select value={selectedClientType} onValueChange={setSelectedClientType}>
              <SelectTrigger className="w-full" data-testid="select-client-type">
                <SelectValue placeholder="Select client type" />
              </SelectTrigger>
              <SelectContent>
                {CLIENT_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Configure late fee rules for <strong>{getClientTypeLabel(selectedClientType)}</strong> clients
            </p>
          </div>

          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : (
            <Tabs defaultValue="fees" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="fees" data-testid="tab-fees">Fee Rules</TabsTrigger>
                <TabsTrigger value="exceptions" data-testid="tab-exceptions">State Exceptions</TabsTrigger>
              </TabsList>

              {/* Fee Rules Tab */}
              <TabsContent value="fees" className="space-y-4">
                {filteredTiers.length === 0 && !isCreatingTier && (
                  <div className="text-center py-8 text-muted-foreground border rounded-lg">
                    <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
                    <p>No late fee rules for {getClientTypeLabel(selectedClientType)} clients.</p>
                    <p className="text-sm">Add your first rule below.</p>
                  </div>
                )}

                {/* Existing Fee Rules */}
                {filteredTiers.map((tier) => (
                  <Card key={tier.id} className={`${editingTier?.id === tier.id ? "ring-2 ring-primary" : ""} ${!tier.isActive ? "opacity-60" : ""}`}>
                    <CardContent className="p-4">
                      {editingTier?.id === tier.id ? (
                        <TierForm 
                          form={tierForm} 
                          setForm={setTierForm} 
                          onSave={handleSaveTier} 
                          onCancel={resetTierForm}
                          isPending={updateTierMutation.isPending}
                          isEdit={true}
                        />
                      ) : (
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="font-medium">{tier.name}</span>
                              <Badge variant="default" className="text-xs" data-testid={`badge-tier-scope-${tier.id}`}>
                                Client type: {tier.clientType}
                              </Badge>
                              {!tier.isActive && <Badge variant="secondary">Inactive</Badge>}
                              {tier.isCumulative && <Badge variant="outline">Cumulative</Badge>}
                            </div>
                            <div className="text-sm text-muted-foreground space-y-1">
                              <div className="flex items-center gap-4">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {tier.daysAfterEvent} days after event
                                </span>
                                <span className="flex items-center gap-1">
                                  {tier.feeType === "percent" ? (
                                    <><Percent className="w-3 h-3" />{tier.feeAmount}%</>
                                  ) : (
                                    <><DollarSign className="w-3 h-3" />{formatCurrency(tier.feeAmount)}</>
                                  )}
                                </span>
                              </div>
                              {tier.isRecurring && (
                                <div className="flex items-center gap-1 text-blue-600">
                                  <RefreshCw className="w-3 h-3" />
                                  Recurring every {tier.recurringIntervalDays} days
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="icon" onClick={() => startEditTier(tier)} data-testid={`button-edit-tier-${tier.id}`}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => deleteTierMutation.mutate(tier.id)} disabled={deleteTierMutation.isPending} data-testid={`button-delete-tier-${tier.id}`}>
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}

                {/* Create New Tier Form */}
                {isCreatingTier && (
                  <Card className="ring-2 ring-primary">
                    <CardContent className="p-4">
                      <TierForm 
                        form={tierForm} 
                        setForm={setTierForm} 
                        onSave={handleSaveTier} 
                        onCancel={resetTierForm}
                        isPending={createTierMutation.isPending}
                        isEdit={false}
                      />
                    </CardContent>
                  </Card>
                )}

                {!isCreatingTier && !editingTier && (
                  <Button 
                    onClick={() => { resetTierForm(); setIsCreatingTier(true); }} 
                    className="w-full"
                    variant="outline"
                    data-testid="button-add-fee-rule"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Late Fee Rule
                  </Button>
                )}
              </TabsContent>

              {/* State Exceptions Tab */}
              <TabsContent value="exceptions" className="space-y-4">
                <div className="p-4 bg-muted rounded-lg text-sm">
                  <p className="font-medium mb-1">State Exceptions Override Fee Rules</p>
                  <p className="text-muted-foreground">
                    Some states limit late fees. Add exceptions here to cap fees for specific states.
                    These caps override the master fee schedule above.
                  </p>
                </div>

                {filteredExceptions.length === 0 && !isCreatingException && (
                  <div className="text-center py-6 text-muted-foreground border rounded-lg">
                    <MapPin className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
                    <p>No state exceptions for {getClientTypeLabel(selectedClientType)} clients.</p>
                  </div>
                )}

                {/* Existing State Exceptions */}
                {filteredExceptions.map((exception) => (
                  <Card key={exception.id} className={`${editingException?.id === exception.id ? "ring-2 ring-primary" : ""} ${!exception.isActive ? "opacity-60" : ""}`}>
                    <CardContent className="p-4">
                      {editingException?.id === exception.id ? (
                        <ExceptionForm 
                          form={exceptionForm} 
                          setForm={setExceptionForm} 
                          onSave={handleSaveException} 
                          onCancel={resetExceptionForm}
                          isPending={updateExceptionMutation.isPending}
                          isEdit={true}
                        />
                      ) : (
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <Badge variant="outline">{exception.state}</Badge>
                              <span className="font-medium">{getStateLabel(exception.state)}</span>
                              <Badge variant="default" className="text-xs" data-testid={`badge-exception-scope-${exception.id}`}>
                                Client type: {exception.clientType}
                              </Badge>
                              {!exception.isActive && <Badge variant="secondary">Inactive</Badge>}
                              {exception.isRecurring && (
                                <Badge variant="outline" className="text-blue-600 border-blue-300">
                                  <RefreshCw className="w-3 h-3 mr-1" />
                                  Recurring
                                </Badge>
                              )}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              Max fee: {exception.maxFeeAmount ? formatCurrency(exception.maxFeeAmount) : ""} 
                              {exception.maxFeeAmount && exception.maxFeePercent ? " or " : ""}
                              {exception.maxFeePercent ? `${exception.maxFeePercent}%` : ""}
                              {exception.notes && <span className="ml-2 italic">({exception.notes})</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="icon" onClick={() => startEditException(exception)} data-testid={`button-edit-exception-${exception.id}`}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => deleteExceptionMutation.mutate(exception.id)} disabled={deleteExceptionMutation.isPending} data-testid={`button-delete-exception-${exception.id}`}>
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}

                {/* Create New Exception Form */}
                {isCreatingException && (
                  <Card className="ring-2 ring-primary">
                    <CardContent className="p-4">
                      <ExceptionForm 
                        form={exceptionForm} 
                        setForm={setExceptionForm} 
                        onSave={handleSaveException} 
                        onCancel={resetExceptionForm}
                        isPending={createExceptionMutation.isPending}
                        isEdit={false}
                      />
                    </CardContent>
                  </Card>
                )}

                {!isCreatingException && !editingException && (
                  <Button 
                    onClick={() => { resetExceptionForm(); setIsCreatingException(true); }} 
                    className="w-full"
                    variant="outline"
                    data-testid="button-add-exception"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add State Exception
                  </Button>
                )}
              </TabsContent>
            </Tabs>
          )}
        </div>
    </>
  );

  if (inPanel) {
    return (
      <div className="w-full h-full flex flex-col overflow-auto p-4 sm:p-6">
        {innerContent}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={dialogClass}>
        {innerContent}
      </DialogContent>
    </Dialog>
  );
}

// Tier Form Component
function TierForm({ 
  form, 
  setForm, 
  onSave, 
  onCancel, 
  isPending,
  isEdit 
}: { 
  form: any; 
  setForm: (form: any) => void; 
  onSave: () => void; 
  onCancel: () => void; 
  isPending: boolean;
  isEdit: boolean;
}) {
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="tier-name">Rule Name</Label>
        <Input
          id="tier-name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="e.g., 30-Day Late Fee"
          data-testid="input-tier-name"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="days-after">Days After Event</Label>
          <Input
            id="days-after"
            type="number"
            min={0}
            value={form.daysAfterEvent}
            onChange={(e) => setForm({ ...form, daysAfterEvent: parseInt(e.target.value) || 0 })}
            data-testid="input-days-after-show"
          />
          <p className="text-xs text-muted-foreground mt-1">Days after the event date before the first fee applies</p>
        </div>
        <div>
          <Label htmlFor="grace-period">Grace Period (days)</Label>
          <Input
            id="grace-period"
            type="number"
            min={0}
            value={form.gracePeriodDays}
            onChange={(e) => setForm({ ...form, gracePeriodDays: parseInt(e.target.value) || 0 })}
            data-testid="input-grace-period"
          />
          <p className="text-xs text-muted-foreground mt-1">Extra days before fee applies</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Fee Type</Label>
          <Select value={form.feeType} onValueChange={(val) => setForm({ ...form, feeType: val })}>
            <SelectTrigger data-testid="select-fee-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="flat">Flat Amount ($)</SelectItem>
              <SelectItem value="percent">Percentage (%)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="fee-amount">{form.feeType === "percent" ? "Percentage" : "Amount"}</Label>
          <div className="relative">
            {form.feeType === "flat" && <DollarSign className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />}
            <Input
              id="fee-amount"
              type="number"
              min={0}
              step={form.feeType === "percent" ? "0.1" : "0.01"}
              value={form.feeAmount}
              onChange={(e) => setForm({ ...form, feeAmount: e.target.value })}
              className={form.feeType === "flat" ? "pl-8" : ""}
              data-testid="input-fee-amount"
            />
            {form.feeType === "percent" && <Percent className="absolute right-2.5 top-2.5 h-4 w-4 text-muted-foreground" />}
          </div>
        </div>
      </div>

      <div className="space-y-3 border-t pt-3">
        <div className="flex items-center gap-3">
          <Checkbox
            id="is-recurring"
            checked={form.isRecurring}
            onCheckedChange={(checked) => setForm({ ...form, isRecurring: !!checked })}
            data-testid="checkbox-recurring"
          />
          <Label htmlFor="is-recurring" className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            Add recurring fee
          </Label>
        </div>
        
        {form.isRecurring && (
          <div className="ml-7">
            <Label htmlFor="recurring-interval">Repeat every</Label>
            <div className="flex items-center gap-2">
              <Input
                id="recurring-interval"
                type="number"
                min={1}
                value={form.recurringIntervalDays}
                onChange={(e) => setForm({ ...form, recurringIntervalDays: parseInt(e.target.value) || 30 })}
                className="w-24"
                data-testid="input-recurring-interval"
              />
              <span className="text-sm text-muted-foreground">days</span>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3">
          <Checkbox
            id="is-cumulative"
            checked={form.isCumulative}
            onCheckedChange={(checked) => setForm({ ...form, isCumulative: !!checked })}
            data-testid="checkbox-cumulative"
          />
          <Label htmlFor="is-cumulative">Cumulative (adds to previous fees)</Label>
        </div>

        <div className="flex items-center gap-3">
          <Switch
            id="is-active"
            checked={form.isActive}
            onCheckedChange={(checked) => setForm({ ...form, isActive: checked })}
            data-testid="switch-active"
          />
          <Label htmlFor="is-active">Active</Label>
        </div>
      </div>

      <div className="flex gap-2 justify-end pt-2">
        <Button variant="outline" size="sm" onClick={onCancel} data-testid="button-cancel">
          Cancel
        </Button>
        <Button size="sm" onClick={onSave} disabled={isPending} data-testid="button-save-tier">
          {isPending ? "Saving..." : isEdit ? "Save Changes" : "Create Rule"}
        </Button>
      </div>
    </div>
  );
}

// Exception Form Component
function ExceptionForm({ 
  form, 
  setForm, 
  onSave, 
  onCancel, 
  isPending,
  isEdit 
}: { 
  form: any; 
  setForm: (form: any) => void; 
  onSave: () => void; 
  onCancel: () => void; 
  isPending: boolean;
  isEdit: boolean;
}) {
  const [stateOpen, setStateOpen] = useState(false);
  const selectedStateLabel = US_STATES.find(s => s.value === form.state)?.label || "";

  return (
    <div className="space-y-4">
      <div>
        <Label>State</Label>
        <Popover open={stateOpen} onOpenChange={setStateOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={stateOpen}
              className="w-full justify-between font-normal"
              data-testid="select-state"
            >
              {form.state ? `${selectedStateLabel} (${form.state})` : "Select a state..."}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full p-0" align="start">
            <Command>
              <CommandInput placeholder="Search states..." data-testid="input-state-search" />
              <CommandList>
                <CommandEmpty>No state found.</CommandEmpty>
                <CommandGroup className="max-h-[200px] overflow-auto">
                  {US_STATES.map((state) => (
                    <CommandItem
                      key={state.value}
                      value={`${state.label} ${state.value}`}
                      onSelect={() => {
                        setForm({ ...form, state: state.value });
                        setStateOpen(false);
                      }}
                      data-testid={`option-state-${state.value}`}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          form.state === state.value ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {state.label} ({state.value})
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="max-fee-amount">Max Fee Amount</Label>
          <div className="relative">
            <DollarSign className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              id="max-fee-amount"
              type="number"
              min={0}
              step="0.01"
              value={form.maxFeeAmount}
              onChange={(e) => setForm({ ...form, maxFeeAmount: e.target.value })}
              className="pl-8"
              placeholder="e.g., 50.00"
              data-testid="input-max-fee-amount"
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1">Maximum flat fee allowed</p>
        </div>
        <div>
          <Label htmlFor="max-fee-percent">Max Fee Percentage</Label>
          <div className="relative">
            <Input
              id="max-fee-percent"
              type="number"
              min={0}
              max={100}
              step="0.1"
              value={form.maxFeePercent}
              onChange={(e) => setForm({ ...form, maxFeePercent: e.target.value })}
              placeholder="e.g., 10"
              data-testid="input-max-fee-percent"
            />
            <Percent className="absolute right-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          </div>
          <p className="text-xs text-muted-foreground mt-1">Maximum % of invoice allowed</p>
        </div>
      </div>

      <div>
        <Label htmlFor="notes">Notes</Label>
        <Input
          id="notes"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          placeholder="e.g., State law limits late fees to 10%"
          data-testid="input-exception-notes"
        />
      </div>

      <div className="space-y-3 border-t pt-3">
        <div className="flex items-center gap-3">
          <Checkbox
            id="exception-recurring"
            checked={form.isRecurring}
            onCheckedChange={(checked) => setForm({ ...form, isRecurring: !!checked })}
            data-testid="checkbox-exception-recurring"
          />
          <Label htmlFor="exception-recurring" className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            Recurring fee (applies repeatedly)
          </Label>
        </div>

        <div className="flex items-center gap-3">
          <Switch
            id="exception-active"
            checked={form.isActive}
            onCheckedChange={(checked) => setForm({ ...form, isActive: checked })}
            data-testid="switch-exception-active"
          />
          <Label htmlFor="exception-active">Active</Label>
        </div>
      </div>

      <div className="flex gap-2 justify-end pt-2">
        <Button variant="outline" size="sm" onClick={onCancel} data-testid="button-cancel-exception">
          Cancel
        </Button>
        <Button size="sm" onClick={onSave} disabled={isPending} data-testid="button-save-exception">
          {isPending ? "Saving..." : isEdit ? "Save Changes" : "Create Exception"}
        </Button>
      </div>
    </div>
  );
}
